import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import {
    auth,
    provider,
    signInWithPopup,
    signInWithCustomToken,
    User,
} from '../auth/firebase';
import {
    getIdTokenResult, getRedirectResult,
    onAuthStateChanged, signInWithRedirect,
    signOut,
} from 'firebase/auth';
import { setAccessToken, setUnauthorizedLogger, setApiBaseUrlGetter } from '../auth/axiosApexInstance';
import axiosApexInstance from '../auth/axiosApexInstance';
import {FavLinkSection, TriageQueueDTO} from "../types";
import {ApexUserMenuConfig} from "../components/userTypes";
import {MISSING_TOKEN_ERROR_MSG} from "../Constants";
import { apexAuthLoginUrl } from '../apexEndpoints';
import { MSAL_AUTHORITY } from '../auth/msalConfig';
import { devLog, devWarn, logClientError } from '../utils/clientSafeLog';


const prodApi = {
    API_URL: "https://api.apexheartcare.com",
    REACT_APP_AUTHENTICATION: "https://api.apexheartcare.com/action",
    REACT_APP_BASE_API: "https://api.apexheartcare.com/action",
    REACT_APEX_BASE_API: "https://api.apexheartcare.com",
    PDFJS_KEY: "8JJ9A6eCpr4Txx6tx880"
};

const localApi = {
    API_URL: "https://localhost:8090",
    REACT_APP_AUTHENTICATION: "https://localhost:8090/action",
    REACT_APP_BASE_API: "https://localhost:8090/action",
    REACT_APEX_BASE_API: "https://localhost:8090",
    PDFJS_KEY: "9LQhM9GRvXPHexJZYq6W"
};

export interface ApexUserDTO {
    userId: number;
    userEcwid: number;
    userEmail: string;
    userSubject: string;
    userFirstName: string;
    userLastName: string;
    userRoles: string;
    createdAt: string;
    delFlag: number;
    isActive: number;
    userFavlinksJson?: string;
    adologin?: string;
    // ECW user ID and name for telephone encounters and document uploads
    ecwUserId?: number;
    ecwName?: string;
    triageQueues: any[];
    allUsers: ApexUserDTO[];
    refreshAllUsers: () => Promise<void>;
    userMenuConfigs: ApexUserMenuConfig[];
    refreshUserMenus: () => Promise<void>;
    deactivateUser: (userId: number, activate?: boolean) => Promise<void>;

}

interface UnauthorizedLog {
    url: string;
    time: string;
}

interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    lastAuthTime: number | null;
    expiresAt: number | null;
    apexUser: ApexUserDTO | null;
    isLoggedIn: boolean;
    signIn: () => Promise<void>;
    signInWithCustomFirebaseToken: (token: string) => Promise<void>;
    setTokenFromMoneyBook: (idToken: string) => Promise<void>;
    signOutUser: () => Promise<void>;
    refreshAccessToken: () => Promise<void>;
    refreshCustomToken: () => Promise<void>;
    minutesLeft: () => number | null;
    unauthorizedLog: UnauthorizedLog[];
    addUnauthorizedLog: (url: string) => void;
    favLinks: FavLinkSection[];
    updateFavLinks: (links: FavLinkSection[]) => Promise<void>;
    triageQueues: TriageQueueDTO[];
    allUsers: ApexUserDTO[];
    refreshAllUsers: () => Promise<void>;
    userMenuConfigs: ApexUserMenuConfig[];
    refreshUserMenus: () => Promise<void>;
    deactivateUser: (userId: number, activate?: boolean) => Promise<void>;
    deleteUser: (userId: number) => Promise<void>;
    adLoading: boolean;
    setAdLoginOpen: (open: boolean) => void;
    isProdApi: boolean;
    toggleApiEnv: () => void;
    /** Same persistence as toggle; for MoneyBook parent postMessage. */
    setApiEnvUseProd: (useProd: boolean) => void;
    apiConfig: typeof prodApi;
    uploadLocal: boolean;
    toggleUploadLocal: () => void;
    useProdForDocs: boolean;
    toggleUseProdForDocs: () => void;
}

const UserContext = createContext<AuthState | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { instance: msalInstance, accounts: msalAccounts, inProgress: msalInProgress } = useMsal();
    const msalHomeAccountId = msalAccounts[0]?.homeAccountId ?? '';
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessTokenState] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [lastAuthTime, setLastAuthTime] = useState<number | null>(null);
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [apexUser, setApexUser] = useState<ApexUserDTO | null>(null);
    const [unauthorizedLog, setUnauthorizedLog] = useState<UnauthorizedLog[]>([]);
    const [favLinks, setFavLinks] = useState<FavLinkSection[]>([]);
    const [triageQueues, setTriageQueues] = useState<TriageQueueDTO[]>([]);
    const [allUsers, setAllUsers] = useState<ApexUserDTO[]>([]);
    const [userMenuConfigs, setUserMenuConfigs] = useState<ApexUserMenuConfig[]>([]);
    const [adLoading] = useState(false);
    const [, setAdLoginOpen] = useState(false);
    const [isProdApi, setIsProdApi] = useState(() => {
        // If either toggle is set to local, use local
        return localStorage.getItem('useProdApi') !== 'false' && localStorage.getItem('uploadLocal') !== 'true';
    });

    // New preference: Always use production for document endpoints
    const [useProdForDocs, setUseProdForDocs] = useState(() => {
        return localStorage.getItem('useProdForDocs') === 'true';
    });

    const toggleUseProdForDocs = () => {
        setUseProdForDocs(prev => {
            const newValue = !prev;
            localStorage.setItem('useProdForDocs', String(newValue));
            return newValue;
        });
    };

    // Unified toggle: toggling either will switch both
    const toggleApiEnv = () => {
        setIsProdApi(prev => {
            const newValue = !prev;
            localStorage.setItem('useProdApi', String(newValue));
            localStorage.setItem('uploadLocal', String(!newValue)); // keep in sync
            return newValue;
        });
    };

    const setApiEnvUseProd = useCallback((useProd: boolean) => {
        localStorage.setItem('useProdApi', String(useProd));
        localStorage.setItem('uploadLocal', String(!useProd));
        setIsProdApi(useProd);
    }, []);
    const toggleUploadLocal = () => {
        setIsProdApi(() => {
            localStorage.setItem('useProdApi', String(false));
            localStorage.setItem('uploadLocal', String(true));
            return false;
        });
    };
    const uploadLocal = !isProdApi;

    const apiConfig = isProdApi ? prodApi : localApi;
    const addUnauthorizedLog = useCallback((url: string) => {
        const timestamp = new Date().toLocaleString();
        setUnauthorizedLog(prev => [...prev, { url, time: timestamp }]);
    }, []);

    // loginMode removed - was unused
    // const loginMode = window !== window.parent ? 'redirect' : 'popup';

    const refreshUserMenus = useCallback(async () => {
        if (!apexUser) return;
        const res = await axiosApexInstance.get<ApexUserMenuConfig[]>(`/apexuser/menu/${apexUser.userId}`);
        setUserMenuConfigs(res.data);
    }, [apexUser]);

    useEffect(() => {
        setApiBaseUrlGetter(() => apiConfig.REACT_APEX_BASE_API);
    }, [apiConfig]);

    useEffect(() => {
        if (apexUser) refreshUserMenus();
    }, [apexUser, refreshUserMenus]);


    const storeAuthData = async (data: any) => {
        const cache = await caches.open('auth-cache');
        await cache.put('user-auth', new Response(JSON.stringify(data)));
    };

    const updateFavLinks = async (newLinks: FavLinkSection[]) => {
        setFavLinks(newLinks);
        await axiosApexInstance.post('/apexuser/favlinks', newLinks, {
            headers: { 'Content-Type': 'application/json' }
        });
    };

    const deactivateUser = async (userId: number, activate: boolean = false) => {
        if (activate) {
            await axiosApexInstance.put(`/apexuser/${userId}/activate`);
        } else {
            await axiosApexInstance.put(`/apexuser/${userId}/deactivate`);
        }
        await refreshAllUsers();
    };

    const deleteUser = async (userId: number) => {
        await axiosApexInstance.delete(`/apexuser/${userId}`);
        await refreshAllUsers();
    };



    const refreshAllUsers = useCallback(async () => {
        if (!accessToken) return;
        try {
            const res = await axiosApexInstance.get<ApexUserDTO[]>('/apexuser/active');
            // Debug logging removed for production
            // console.log('🔍 Raw API response for all users:', res.data);
            // console.log('🔍 Total users returned:', res.data.length);
            
            // Log each user's ECW data (removed - too verbose)
            // res.data.forEach((user, index) => {
            //     console.log(`🔍 User ${index + 1}:`, {
            //         userId: user.userId,
            //         firstName: user.userFirstName,
            //         lastName: user.userLastName,
            //         adologin: user.adologin,
            //         ecwUserId: user.ecwUserId,
            //         ecwName: user.ecwName,
            //         hasEcwUserId: !!user.ecwUserId,
            //         hasEcwName: !!user.ecwName,
            //         ecwUserIdValid: user.ecwUserId && user.ecwUserId > 0,
            //         ecwNameValid: user.ecwName && user.ecwName.trim() !== ''
            //     });
            // });
            
            // TEMPORARILY: Don't filter users to see what we get
            // const validUsers = res.data.filter(user => 
            //     user.ecwUserId && 
            //     user.ecwUserId > 0 && 
            //     user.ecwName && 
            //     user.ecwName.trim() !== ''
            // );
            
            // Debug logging removed for production
            // const allUsersWithValidity = res.data.map(user => ({
            //     ...user,
            //     isValid: !!(user.ecwUserId && user.ecwUserId > 0 && user.ecwName && user.ecwName.trim() !== '')
            // }));
            // console.log('🔍 All users with validity:', allUsersWithValidity);
            // console.log('🔍 Users without ECW data:', allUsersWithValidity.filter(u => !u.isValid));
            
            setAllUsers(res.data); // Use all users for now
        } catch (error: any) {
            // Suppress error log if it's due to missing access token (pre-login case)
            if (error?.message === MISSING_TOKEN_ERROR_MSG ) {
                return;
            }

            logClientError('fetchAllActiveUsers', error);
        }

    }, [accessToken]);


    const handleFirebaseLogin = useCallback(async (firebaseUser: User) => {
        const idTokenResult = await getIdTokenResult(firebaseUser);
        const expirationTimestamp = idTokenResult.claims.exp ? Number(idTokenResult.claims.exp) * 1000 : Date.now() + 3600000;
        // Debug logging removed for production
        // console.log("Got Firebase User", idTokenResult, expirationTimestamp);

        const newAuthData = {
            user: firebaseUser,
            accessToken: idTokenResult.token,
            refreshToken: await firebaseUser.getIdToken(true),
            lastAuthTime: Date.now(),
            expiresAt: expirationTimestamp,
        };

        setUser(firebaseUser);
        setAccessTokenState(newAuthData.accessToken);
        setRefreshToken(newAuthData.refreshToken);
        setLastAuthTime(newAuthData.lastAuthTime);
        setExpiresAt(newAuthData.expiresAt);

        await storeAuthData(newAuthData);
    }, []);

    const signIn = async () => {
        const isInIframe = window !== window.parent;

        try {
            if (isInIframe) {
                // Teams iframe — use redirect login
                await signInWithRedirect(auth, provider);
            } else {
                // Browser — use popup login
                const result = await signInWithPopup(auth, provider);
                // Debug logging removed for production
                // console.log("AD User:", result.user);
                await handleFirebaseLogin(result.user);
            }
        } catch (error) {
            logClientError('googleSignIn', error);
        }
    };


    useEffect(() => {
        if (user) {

            axiosApexInstance.get('/task/triage/triagequeues').then(res => {
                setTriageQueues(res.data);
                // Debug logging removed for production
                // console.log("Received Triage Queue", res.data);
            });
        }
    }, [user]);

    const signInWithCustomFirebaseToken = useCallback(async (customToken: string) => {
        try {
            const result = await signInWithCustomToken(auth, customToken);
            await handleFirebaseLogin(result.user);
        } catch (error) {
            logClientError('customTokenSignIn', error);
        }
    }, [handleFirebaseLogin]);

    /** One automatic Entra→Firebase attempt per (no Firebase user + MSAL account) episode; reset when MSAL account changes. */
    const entraAutoBridgeDoneRef = useRef(false);
    const prevFirebaseUserRef = useRef<User | null>(null);
    /** True while {@link signOutUser} runs — prevents Entra bridge from re-logging in during Firebase sign-out. */
    const signingOutRef = useRef(false);
    /** True during logout until MSAL session is cleared — blocks Entra→Firebase auto bridge. */
    const skipEntraAutoBridgeRef = useRef(false);

    useEffect(() => {
        entraAutoBridgeDoneRef.current = false;
    }, [msalHomeAccountId]);

    useEffect(() => {
        if (prevFirebaseUserRef.current && !user) {
            entraAutoBridgeDoneRef.current = false;
        }
        prevFirebaseUserRef.current = user;
    }, [user]);

    /**
     * After MSAL redirect/popup, we only have an Entra session — not Firebase / Apex.
     * If MSAL has an account but Firebase does not, exchange the Entra ID token for a
     * Firebase custom token (same as LoginAD), then existing effects load /apexuser/me and menus.
     */
    useEffect(() => {
        if (skipEntraAutoBridgeRef.current) return;
        if (user) return;
        if (msalInProgress !== InteractionStatus.None) return;
        if (!msalHomeAccountId) return;
        const account = msalInstance.getAccount({ homeAccountId: msalHomeAccountId });
        if (!account) return;
        if (entraAutoBridgeDoneRef.current) return;
        entraAutoBridgeDoneRef.current = true;

        const ac = new AbortController();
        (async () => {
            try {
                const silent = await msalInstance.acquireTokenSilent({
                    scopes: ['openid', 'profile', 'email'],
                    account,
                    authority: MSAL_AUTHORITY,
                });
                const idToken = silent.idToken;
                if (!idToken || ac.signal.aborted) return;

                const email =
                    silent.account?.username?.trim() ||
                    account.username?.trim() ||
                    '';
                const res = await fetch(apexAuthLoginUrl(apiConfig.REACT_APEX_BASE_API), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password: idToken }),
                    signal: ac.signal,
                });
                const data = await res.json();
                if (ac.signal.aborted) return;
                if (data.customToken) {
                    await signInWithCustomFirebaseToken(data.customToken);
                }
            } catch (e: unknown) {
                if (ac.signal.aborted) return;
                const err = e as { name?: string; errorCode?: string };
                if (err?.name === 'AbortError') return;
                // e.g. interaction_required — user can still use "Sign in with Microsoft"
                devLog('[Entra→Firebase] Silent bridge skipped:', err?.errorCode ?? e);
            }
        })();
        return () => ac.abort();
    }, [
        user,
        msalInProgress,
        msalHomeAccountId,
        msalInstance,
        apiConfig.REACT_APEX_BASE_API,
        signInWithCustomFirebaseToken,
    ]);

    /**
     * Sync UI when Firebase session is gone — does NOT sign out of Microsoft.
     * Must not call {@link #signOutUser} from here: that runs MSAL logout and caused a loop
     * (every page load with no Firebase user → logoutRedirect → Microsoft "pick account to sign out" → repeat).
     */
    const clearLocalAuthStateOnly = useCallback(() => {
        setAccessToken(null);
        setUser(null);
        setAccessTokenState(null);
        setRefreshToken(null);
        setLastAuthTime(null);
        setExpiresAt(null);
        setApexUser(null);
        void caches
            .open('auth-cache')
            .then((c) => c.delete('user-auth'))
            .catch(() => {});
    }, []);

    const signOutUser = useCallback(async () => {
        if (signingOutRef.current) {
            return;
        }
        signingOutRef.current = true;
        skipEntraAutoBridgeRef.current = true;
        let msalLogoutRedirecting = false;
        try {
            clearLocalAuthStateOnly();
            try {
                await signOut(auth);
            } catch (e) {
                devWarn('[signOut] Firebase signOut:', e);
            }

            // Clear Microsoft (Entra) session — only on explicit user logout (not on every Firebase=null event).
            const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
            if (account) {
                entraAutoBridgeDoneRef.current = false;
                // Prefer popup so we avoid a full-page redirect loop with handleRedirectPromise / reload.
                try {
                    await msalInstance.logoutPopup({
                        account,
                        postLogoutRedirectUri: window.location.origin,
                    });
                } catch (popupErr) {
                    devWarn('[signOut] logoutPopup failed, using redirect', popupErr);
                    msalLogoutRedirecting = true;
                    await msalInstance.logoutRedirect({
                        account,
                        postLogoutRedirectUri: window.location.origin,
                    });
                    return;
                }
            }
        } catch (e) {
            logClientError('signOut', e);
        } finally {
            if (!msalLogoutRedirecting) {
                signingOutRef.current = false;
                skipEntraAutoBridgeRef.current = false;
            }
        }
    }, [msalInstance, clearLocalAuthStateOnly]);

    const refreshAccessToken = useCallback(async () => {
        if (!user) return;
        try {
            const newToken = await user.getIdToken(true);
            const idTokenResult = await getIdTokenResult(user);
            const expirationTimestamp = idTokenResult.claims.exp ? Number(idTokenResult.claims.exp) * 1000 : Date.now() + 3600000;

            setAccessTokenState(newToken);
            setExpiresAt(expirationTimestamp);

            await storeAuthData({
                user,
                accessToken: newToken,
                refreshToken,
                lastAuthTime,
                expiresAt: expirationTimestamp,
            });
        } catch (error) {
            logClientError('tokenRefresh', error);
        }
    }, [user, refreshToken, lastAuthTime]);

    const refreshCustomToken = useCallback(async () => {
        try {
            const res = await fetch('/auth/refresh', { method: 'POST', credentials: 'include' });
            if (!res.ok) throw new Error('Unable to refresh custom token');
            const { customToken } = await res.json();
            const result = await signInWithCustomToken(auth, customToken);
            await handleFirebaseLogin(result.user);
        } catch (error) {
            logClientError('customTokenRefresh', error);
            await signOutUser();
        }
    }, [handleFirebaseLogin, signOutUser]);

    const setTokenFromMoneyBook = useCallback(async (idToken: string) => {
        setAccessToken(idToken);
        setAccessTokenState(idToken);
        setUser(null);
        setRefreshToken(null);
        setLastAuthTime(null);
        setExpiresAt(null);
        try {
            const res = await axiosApexInstance.get<ApexUserDTO>('/apexuser/me');
            const data = res.data;
            setApexUser(data);
            if (data.userFavlinksJson) {
                try {
                    setFavLinks(JSON.parse(data.userFavlinksJson));
                } catch {
                    setFavLinks([]);
                }
            } else {
                setFavLinks([]);
            }
            refreshAllUsers();
        } catch (err) {
                logClientError('apexUserFromMoneyBookToken', err);
        }
    }, [refreshAllUsers]);

    const minutesLeft = useCallback(() => {
        if (!expiresAt) return null;
        return Math.round((expiresAt - Date.now()) / 60000);
    }, [expiresAt]);

    useEffect(() => {
        const interval = setInterval(async () => {
            const minsLeft = minutesLeft();
            if (minsLeft !== null && minsLeft <= 5) {
                const isCustomAuth = user?.providerData?.[0]?.providerId === 'custom';
                isCustomAuth ? await refreshCustomToken() : await refreshAccessToken();
            }
        }, 60000);
        return () => clearInterval(interval);
    }, [expiresAt, minutesLeft, refreshAccessToken, refreshCustomToken, user?.providerData]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (!firebaseUser) {
                // Do not call signOutUser() here — that runs MSAL logoutRedirect and caused an infinite
                // Microsoft "choose account to sign out" loop on every load when Firebase was null.
                if (signingOutRef.current) {
                    return;
                }
                clearLocalAuthStateOnly();
                return;
            }
            await handleFirebaseLogin(firebaseUser);
        });
        return () => unsubscribe();
    }, [handleFirebaseLogin, clearLocalAuthStateOnly]);

    useEffect(() => {
        if (accessToken) {
            setAccessToken(accessToken);
            axiosApexInstance
                .get<ApexUserDTO>('/apexuser/me')
                .then((res) => {
                    setApexUser(res.data);

                    // ✅ Parse favLinks JSON and update state
                    if (res.data.userFavlinksJson) {
                        try {
                            const parsed = JSON.parse(res.data.userFavlinksJson);
                            setFavLinks(parsed);
                        } catch (e) {
                            devWarn('Failed to parse favLinks JSON:', e);
                            setFavLinks([]);
                        }
                    } else {
                        setFavLinks([]);
                    }
                    
                    // Only refresh all users after we have the apex user
                    refreshAllUsers();
                })
                .catch((err) => {
                    logClientError('fetchApexUser', err);
                });
        }
    }, [accessToken, apiConfig.REACT_APEX_BASE_API, refreshAllUsers]);

    useEffect(() => {
        setUnauthorizedLogger(addUnauthorizedLog); // wire into axios interceptor
    }, [addUnauthorizedLog]);

    useEffect(() => {
        getRedirectResult(auth)
            .then((result) => {
                if (result?.user) {
                    handleFirebaseLogin(result.user);
                }
            })
            .catch((error) => {
                logClientError('redirectResult', error);
            });
    }, [handleFirebaseLogin]);


    return (
        <UserContext.Provider
            value={{
                user,
                accessToken,
                refreshToken,
                lastAuthTime,
                expiresAt,
                apexUser,
                isLoggedIn: !!(user || (accessToken && apexUser)),
                signIn,
                signInWithCustomFirebaseToken,
                setTokenFromMoneyBook,
                signOutUser,
                refreshAccessToken,
                refreshCustomToken,
                minutesLeft,
                unauthorizedLog,
                addUnauthorizedLog,
                favLinks,
                updateFavLinks ,
                triageQueues,
                allUsers,
                refreshAllUsers,
                userMenuConfigs,
                refreshUserMenus,
                deactivateUser,
                deleteUser,
                adLoading,
                setAdLoginOpen,
                isProdApi,
                toggleApiEnv,
                setApiEnvUseProd,
                apiConfig,
                uploadLocal,
                toggleUploadLocal,
                useProdForDocs,
                toggleUseProdForDocs,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) throw new Error('useUser must be used within a UserProvider');
    return context;
};
